import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { UtilsHelper } from '@helpers/utils.helper';
import { Device } from '@models/device';
import { UserDevice } from '@models/user-device';
import { StorageService } from '@services/storage.service';
import { DateTime } from 'luxon';

@Component({
  standalone: false,
  selector: 'app-info-stack',
  templateUrl: './info-stack.component.html',
  styleUrls: ['./info-stack.component.scss'],
})
export class InfoStackComponent implements OnInit, OnChanges {
  @Input() device!: Device;
  @Input() userDevice!: UserDevice;

  @Output() refreshRequested = new EventEmitter<void>();

  showFlagError: boolean = true;
  showErrorId: boolean = true;

  displayedColumns: string[] = [
    'id',
    'lastDataDate',
    'type',
    'rssi',
    'voltage',
    'error',
    'period',
  ];
  dataSource: any[] = [];

  nodeColumns: string[] = [
    'node',
    'softwareVersion',
    'hardwareId',
    'module',
    'sensorType',
    'voltage',
    'error',
    'period',
    'previousDataDate',
    'lastDataDate',
    'actions',
  ];
  nodeData: any[] = [];

  constructor(private router: Router, private storageService: StorageService) {}

  ngOnInit(): void {
    this.processDeviceData();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.device && !changes.device.firstChange) {
      this.processDeviceData();
    }
  }
  private processDeviceData(): void {
    if (!this.device) return;

    this.dataSource = [
      {
        id: this.device.externalId,
        type: this.device.extraInfo?.sendBy?.value,
        error: this.device.extraInfo?.errorId?.value,
        period: this.device.extraInfo?.period?.value,
        voltage: this.device.extraInfo?.vBus?.value,
        rssi: this.device.extraInfo?.rssiLV?.value,
        lastDataDate: this.device.status?.lastConnection,
      },
    ];

    this.nodeData = this.device.sensors.map((sensor) => {
      const rawDate = sensor.lastData?.date || null;
      const rawPreviousDate = sensor.previousDataDate || null;
      const timezone = sensor.lastData?.sensors?.[0]?.timezone || 'UTC';

      const lastDataDate =
        typeof rawDate === 'string'
          ? DateTime.fromISO(rawDate, { zone: 'utc' })
              .setZone(timezone)
              .toJSDate()
          : null;

      const previousDataDate =
        typeof rawPreviousDate === 'string'
          ? DateTime.fromISO(rawPreviousDate, { zone: 'utc' })
              .setZone(timezone)
              .toJSDate()
          : null;

      const lastSensorData = sensor.lastData?.sensors?.[0] || {};

      return {
        node: sensor.position,
        softwareVersion: sensor.extraInfo?.softwareVersion?.value,
        hardwareId: sensor.extraInfo?.hardwareId?.value,
        voltage: sensor.extraInfo?.vBattery?.value,
        module: UtilsHelper.nameModule(
          sensor.extraInfo?.moduleConnected?.value
        ),
        sensorType: UtilsHelper.nameSensor(sensor.type).name,
        period: sensor.extraInfo?.period?.value,
        error: sensor.extraInfo?.flagError?.value,
        lastDataDate,
        previousDataDate,
        lastSensorData: lastSensorData,
      };
    });
  }

  getSensorKeys(node: any): string[] {
    return Object.keys(node);
  }

  isSensorValue(key: string, type?: number): boolean {
    const exclude = ['kind', 'type', 'sensorId', 'timezone', 'voltage'];
    // mostar'voltage' para ciertos sensores
    if (key === 'voltage' && [48, 49, 50, 51].includes(type ?? -1)) {
      return true;
    }

    return !exclude.includes(key);
  }

  onRefreshClick() {
    this.refreshRequested.emit(); // esto le dice al padre que actualice los datos
  }

  get showConnectionNote(): boolean {
    return true; // o lógica condicional según tus necesidades
  }

  getNodeErrorMessages(): { node: number; message: string }[] {
    return this.nodeData
      .filter((node) => node.error > 0)
      .map((node) => {
        let message = '';

        switch (node.error) {
          case '1':
            message =
              'Error de comunicación con su sonda en su última medida periódica.';
            break;
          case '2':
            message =
              'El Node se ha reseteado (p.e. al quitar/poner alimentación o con comando softReset(). Este flag se quita al realizar la primera medida periódica.';
            break;
          default:
            message = 'Error desconocido.';
            break;
        }

        return { node: node.node, message };
      });
  }

  dismissFlagError(): void {
    this.showFlagError = false;
  }

  navigateToNode(item) {
    if (this.isSuperAdmin()) {
      const url: string = `intranet/dashboard/user/me/device/${this.device.id}/${item.hardwareId}`;
      this.router.navigate([url]);
    } else {
      const url: string = `intranet/dashboard/${this.userDevice.id}/${item.hardwareId}`;
      this.router.navigate([url]);
    }
  }

  isSuperAdmin(): boolean {
    const role = this.storageService.get('roleName').toLowerCase();
    return role === 'superadmin' || role === 'admin';
  }

  getErrorIdMessages(): { code: string; message: string }[] {
    const error = this.dataSource[0]?.error;

    if (!error || Number(error) === 0) return [];

    let message = '';
    const code = String(error);

    switch (code) {
      case '1':
        message = 'Argumento de comando recibido incorrecto.';
        break;
      case '2':
        message =
          'No se ha realizado la medida debido a que la alimentación está fuera del rango aceptado por la sonda.';
        break;
      case '3':
        message = 'Error de comunicación con la sonda.';
        break;
      case '4':
        message =
          'El número de Nodes del stack es mayor a 8. Reducir el número y volver a ordenar.';
        break;
      case '5':
        message = 'Se ha quitado/puesto un Node. Necesario volver a ordenar.';
        break;
      case '6':
        message = 'Error de comunicación entre nodes';
        break;
      default:
        message = 'Error desconocido.';
        break;
    }

    return [{ code, message }];
  }

  dismissErrorId(): void {
    this.showErrorId = false;
  }
}
